import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  Form,
  InputNumber,
  DatePicker,
  Tooltip,
} from 'antd';
import {
  InfoCircleOutlined,
  PercentageOutlined,
  TagOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { LockedFieldKey } from '../../utils/voucherFormHelpers';

const { TextArea } = Input;

export type DiscountType = 'percentage' | 'fixed_amount';
export type InitialStatus = 'active_immediately' | 'schedule';

export interface VoucherFormValues {
  code?: string;
  campaignName?: string;
  description?: string;
  discountType?: DiscountType;
  discountValue?: number;
  minimumSpend?: number;
  maxDiscountAmount?: number;
  validFrom?: dayjs.Dayjs | string;
  validUntil?: dayjs.Dayjs | string;
  initialStatus?: InitialStatus;
}

export interface VoucherFormProps {
  mode: 'create' | 'edit';
  initialValues?: VoucherFormValues;
  lockedFields?: Set<LockedFieldKey>;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

const LOCKED_TOOLTIP = 'Không thể chỉnh sửa sau khi tạo';

function randomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 8; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return s;
}

export const VoucherForm: React.FC<VoucherFormProps> = ({
  mode,
  initialValues,
  lockedFields = new Set(),
  onSubmit,
  onCancel,
  submitLabel = 'Create Voucher',
}) => {
  const [form] = Form.useForm();
  const [discountType, setDiscountType] = useState<DiscountType>(
    (initialValues?.discountType as DiscountType) || 'percentage'
  );
  const [initialStatus, setInitialStatus] = useState<InitialStatus>(
    (initialValues?.initialStatus as InitialStatus) || 'active_immediately'
  );
  const [submitting, setSubmitting] = useState(false);

  const isLocked = (field: LockedFieldKey) => lockedFields.has(field);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setDiscountType(
        (initialValues.discountType as DiscountType) || 'percentage'
      );
      setInitialStatus(
        (initialValues.initialStatus as InitialStatus) || 'active_immediately'
      );
    } else {
      form.setFieldsValue({
        discountType: 'percentage',
        initialStatus: 'active_immediately',
        minimumSpend: 0,
      });
    }
  }, [initialValues, form]);

  const handleGenerateCode = () => {
    if (!isLocked('code')) form.setFieldValue('code', randomCode());
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      setSubmitting(true);
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={
        initialValues || {
          discountType: 'percentage',
          initialStatus: 'active_immediately',
          minimumSpend: 0,
        }
      }
    >
      <div className="create-voucher-section create-voucher-section-gap">
        <div className="create-voucher-card-title">
          <InfoCircleOutlined className="create-voucher-card-title-icon" />
          <span>Basic Information</span>
        </div>

        <Form.Item
          name="campaignName"
          label="Campaign Name"
          rules={[{ required: true, message: 'Campaign name is required' }]}
          extra="Internal name for tracking this voucher campaign."
        >
          <Input
            placeholder="e.g. Summer Sale 2023"
            className="create-voucher-input"
            size="large"
            disabled={isLocked('campaignName')}
            {...(isLocked('campaignName') && {
              addonAfter: (
                <Tooltip title={LOCKED_TOOLTIP}>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Locked</span>
                </Tooltip>
              ),
            })}
          />
        </Form.Item>

        <div className="create-voucher-row-2">
          <Form.Item
            name="code"
            label="Voucher Code"
            rules={[
              {
                required: mode === 'create',
                message: 'Voucher code is required',
              },
              {
                pattern: /^[A-Z0-9]+$/,
                message: 'Only uppercase letters and numbers',
              },
              { min: 3, max: 50, message: '3-50 characters' },
            ]}
            className="create-voucher-code-item"
          >
            <Input
              placeholder="E.G. SUMMER50"
              className="create-voucher-input-with-btn"
              size="large"
              disabled={isLocked('code')}
              onChange={e => {
                if (!isLocked('code')) {
                  form.setFieldValue('code', e.target.value.toUpperCase());
                }
              }}
              addonAfter={
                mode === 'create' && !isLocked('code') ? (
                  <Button
                    type="primary"
                    size="middle"
                    onClick={handleGenerateCode}
                    className="create-voucher-generate-btn"
                  >
                    Generate
                  </Button>
                ) : isLocked('code') ? (
                  <Tooltip title={LOCKED_TOOLTIP}>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>
                      Locked
                    </span>
                  </Tooltip>
                ) : null
              }
            />
          </Form.Item>
          {mode === 'create' && (
            <Form.Item
              name="initialStatus"
              label="Initial Status"
              className="create-voucher-status-item"
            >
              <Select
                size="large"
                placeholder="Select status"
                options={[
                  { value: 'active_immediately', label: 'Active Immediately' },
                  { value: 'schedule', label: 'Schedule' },
                ]}
                onChange={(v: InitialStatus) => setInitialStatus(v)}
              />
            </Form.Item>
          )}
        </div>

        {(mode === 'edit' ||
          (mode === 'create' && initialStatus === 'schedule')) && (
          <div className="create-voucher-row-2">
            <Form.Item
              name="validFrom"
              label="Valid From"
              rules={[
                {
                  required:
                    mode === 'create' ? initialStatus === 'schedule' : false,
                  message: 'Required when scheduled',
                },
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                className="full-width"
                size="large"
                disabled={isLocked('validFrom')}
              />
            </Form.Item>
            <Form.Item
              name="validUntil"
              label="Valid Until"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                className="full-width"
                size="large"
              />
            </Form.Item>
          </div>
        )}

        <Form.Item name="description" label="Description">
          <TextArea
            placeholder="Enter voucher terms and description visible to users..."
            rows={4}
            className="create-voucher-textarea"
          />
        </Form.Item>
      </div>

      <div className="create-voucher-section create-voucher-section-gap">
        <div className="create-voucher-card-title">
          <PercentageOutlined className="create-voucher-card-title-icon" />
          <span>Discount Details</span>
        </div>

        <Form.Item name="discountType" label="Discount Type">
          <div className="create-voucher-type-cards">
            <button
              type="button"
              className={
                discountType === 'percentage'
                  ? 'create-voucher-type-card create-voucher-type-card-active'
                  : 'create-voucher-type-card'
              }
              disabled={isLocked('discountType')}
              onClick={() => {
                if (!isLocked('discountType')) {
                  setDiscountType('percentage');
                  form.setFieldValue('discountType', 'percentage');
                }
              }}
            >
              <span className="create-voucher-type-card-radio">
                {discountType === 'percentage' ? (
                  <span className="create-voucher-type-card-radio-dot create-voucher-type-card-radio-dot-selected" />
                ) : (
                  <span className="create-voucher-type-card-radio-dot" />
                )}
              </span>
              <span className="create-voucher-type-card-title">
                Percentage (%)
              </span>
              <span className="create-voucher-type-card-subtitle">
                Discount based on % of cart
              </span>
            </button>
            <button
              type="button"
              className={
                discountType === 'fixed_amount'
                  ? 'create-voucher-type-card create-voucher-type-card-active'
                  : 'create-voucher-type-card'
              }
              disabled={isLocked('discountType')}
              onClick={() => {
                if (!isLocked('discountType')) {
                  setDiscountType('fixed_amount');
                  form.setFieldValue('discountType', 'fixed_amount');
                }
              }}
            >
              <span className="create-voucher-type-card-radio">
                {discountType === 'fixed_amount' ? (
                  <span className="create-voucher-type-card-radio-dot create-voucher-type-card-radio-dot-selected" />
                ) : (
                  <span className="create-voucher-type-card-radio-dot" />
                )}
              </span>
              <span className="create-voucher-type-card-title">
                Flat Amount ($)
              </span>
              <span className="create-voucher-type-card-subtitle">
                Fixed deduction amount
              </span>
            </button>
          </div>
        </Form.Item>

        <div className="create-voucher-row-2">
          <Form.Item
            name="discountValue"
            label="Discount Value"
            tooltip={isLocked('discountValue') ? LOCKED_TOOLTIP : undefined}
            rules={[
              {
                required: true,
                message: 'Required',
              },
              {
                validator: (_, value) => {
                  if (value === null || value === undefined || value === '') {
                    return Promise.reject(new Error('Required'));
                  }
                  const num = Number(value);
                  if (isNaN(num) || num <= 0) {
                    return Promise.reject(
                      new Error('Discount Value must be greater than 0')
                    );
                  }
                  if (discountType === 'percentage' && (num > 100 || num < 0)) {
                    return Promise.reject(
                      new Error('Percentage must be between 0 and 100')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            className="create-voucher-discount-value-item"
          >
            <InputNumber
              min={discountType === 'percentage' ? 0 : 0}
              max={discountType === 'percentage' ? 100 : undefined}
              precision={2}
              addonBefore={
                discountType === 'percentage' ? '%' : <TagOutlined />
              }
              className="create-voucher-input-number full-width"
              placeholder={
                discountType === 'percentage' ? 'e.g. 10' : 'e.g. 5.00'
              }
              disabled={isLocked('discountValue')}
            />
          </Form.Item>
          <Form.Item
            name="minimumSpend"
            label="Minimum Spend ($)"
            className="create-voucher-min-spend-item"
          >
            <InputNumber
              min={0}
              precision={2}
              addonBefore={<DollarOutlined />}
              className="create-voucher-input-number full-width"
              placeholder="0"
            />
          </Form.Item>
        </div>

        <Form.Item
          name="maxDiscountAmount"
          label="Max Discount Amount ($)"
          className="create-voucher-max-discount-item"
          extra="Optional. Only for percentage discounts."
        >
          <InputNumber
            min={0}
            precision={2}
            disabled={
              discountType === 'fixed_amount' || isLocked('maxDiscountAmount')
            }
            className="create-voucher-input-number full-width"
            placeholder="leave empty for no limit"
          />
        </Form.Item>
      </div>

      <div className="create-voucher-actions">
        <Button
          type="default"
          onClick={onCancel}
          className="create-voucher-btn-cancel"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          className="create-voucher-btn-submit"
        >
          {submitLabel}
        </Button>
      </div>
    </Form>
  );
};
